import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { app } from "@/lib/firebase";
import { useTranslation } from "react-i18next";
import { AuthHeader } from "@/components/AuthHeader";

// Create a function that returns a form schema with translations
const createFormSchema = (t: (key: string) => string) => {
  return z.object({
    email: z.string().email({ message: t('validation.invalidEmail') }),
  });
};

type FormData = {
  email: string;
};

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth(app);

  const formSchema = createFormSchema(t);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      setIsLoading(true);
      // Dev debug: log intent and environment
      if (import.meta.env.DEV) {
        console.debug('[ForgotPassword] Attempting password reset', {
          email: data.email,
          host: window.location.host,
          isDev: import.meta.env.DEV,
        });
      }

      // Set the email language to match the current UI language (built-in Firebase templates support many locales)
      auth.languageCode = i18n.language;

      // Ensure the email link will redirect back to our app after the user clicks it
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false as const,
      };

      await sendPasswordResetEmail(auth, data.email, actionCodeSettings);

      if (import.meta.env.DEV) {
        console.debug('[ForgotPassword] Password reset email requested successfully', {
          email: data.email,
        });
      }
      setEmailSent(true);
    } catch (err) {
      // Dev debug: capture firebase error code and message
      const e = err as { code?: string; message?: string };
      if (import.meta.env.DEV) {
        console.debug('[ForgotPassword] Password reset error (dev)', {
          code: e?.code,
          message: e?.message,
        });
      }
      console.error("Password reset error:", err);
      setError(t('forgotPassword.errors.sendFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <>
        <AuthHeader />
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-0 bg-background pt-16">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">{t('forgotPassword.checkEmail.title')}</h1>
              <p className="text-muted-foreground">
                {t('forgotPassword.checkEmail.message')}
              </p>
            </div>
            <Button
              onClick={() => navigate("/login")}
              className="w-full mt-6"
            >
              {t('common.actions.backToLogin')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthHeader />
      <div className="min-h-screen flex flex-col sm:flex-row">
        <div className="hidden sm:flex sm:flex-1 bg-primary items-center justify-center">
          <div className="p-8 max-w-md text-primary-foreground animate-fade-in">
            <h1 className="text-3xl font-bold mb-6">{t('forgotPassword.title')}</h1>
            <p className="text-primary-foreground/80 mb-8">
              {t('forgotPassword.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8 bg-background animate-fade-in pt-16">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight">
              {t('forgotPassword.formTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              {t('forgotPassword.formSubtitle')}
            </p>
          </div>

          <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-foreground"
                >
                  {t('common.fields.email')}
                </label>
                <div className="mt-2">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('common.placeholders.email')}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-destructive">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={isLoading}
                >
                  {isLoading ? t('common.actions.sending') : t('forgotPassword.actions.sendResetLink')}
                </Button>
              </div>
            </form>

            <p className="mt-10 text-center text-sm text-muted-foreground">
              <Link to="/login" className="inline-flex items-center gap-2 font-medium text-primary hover:text-primary/90">
                <ArrowLeft className="h-4 w-4" />
                {t('common.actions.backToLogin')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
